import axios, { AxiosInstance } from 'axios';

const baseURL: string =
  process.env.VUE_APP_BACKEND_URL ?? 'http://localhost:1337';

/**
 * Serializes a nested params object to a bracket-notation query string
 * that Strapi's built-in qs parser understands.
 *
 * Examples:
 *   { filters: { lote: { id: { $eq: 2 } } } }
 *   → filters[lote][id][$eq]=2
 *
 *   { populate: ['granja', 'lote'] }
 *   → populate[0]=granja&populate[1]=lote
 */
function serializeParams(params: any, prefix = ''): string {
  if (params === null || params === undefined) return '';
  const parts: string[] = [];

  for (const [key, value] of Object.entries(params as Record<string, any>)) {
    if (value === null || value === undefined) continue;
    const fullKey = prefix ? `${prefix}[${key}]` : key;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item !== null && item !== undefined) {
          if (typeof item === 'object') {
            const part = serializeParams(item, `${fullKey}[${index}]`);
            if (part) parts.push(part);
          } else {
            parts.push(`${encodeURIComponent(`${fullKey}[${index}]`)}=${encodeURIComponent(String(item))}`);
          }
        }
      });
    } else if (typeof value === 'object') {
      const part = serializeParams(value, fullKey);
      if (part) parts.push(part);
    } else {
      parts.push(`${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`);
    }
  }

  return parts.join('&');
}

const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  // Sin límite, una conexión que se queda a medias deja la pantalla girando
  // para siempre y solo se sale recargando. Con él, el fallo se ve y se puede
  // reintentar. 30 s da margen de sobra a las consultas más pesadas (el
  // resumen semanal y la jerarquía) sin castigar una red lenta de granja.
  timeout: 30000,
  paramsSerializer: { serialize: (p) => serializeParams(p) },
});

/**
 * Handler de sesión expirada. Se registra desde App.vue (que sí tiene acceso a
 * router y store sin crear imports circulares). Ante un 401 en cualquier request
 * de datos, se invoca para cerrar la sesión local y llevar al usuario al login
 * con un aviso, en lugar de dejar la app "vacía" fallando en silencio.
 */
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

// Los endpoints de autenticación se excluyen: un 401 en login son credenciales
// inválidas (lo maneja la pantalla de login) y el logout puede responder 401
// con la sesión ya vencida sin que eso sea un error.
function isAuthEndpoint(url: string | undefined): boolean {
  const path = String(url ?? '');
  return path.includes('/api/auth/local') || path.includes('/api/auth/logout');
}

// Cualquier mutación (POST/PUT/DELETE) puede cambiar datos cacheados (p. ej. la
// jerarquía del cliente). Avisamos para invalidar caches de lectura.
api.interceptors.response.use(
  (response) => {
    try {
      const method = String(response.config?.method ?? '').toLowerCase();
      if (method && method !== 'get' && typeof window !== 'undefined') {
        window.dispatchEvent(new Event('aviar:data-changed'));
      }
    } catch {
      /* noop */
    }
    return response;
  },
  (error) => {
    try {
      if (error?.response?.status === 401 && !isAuthEndpoint(error?.config?.url)) {
        unauthorizedHandler?.();
      }
    } catch {
      /* noop: nunca enmascarar el error original */
    }
    return Promise.reject(error);
  }
);

export default api;
